/**
 * Crazy Eights — server-authoritative rules engine (4-player shedding game, individual scoring).
 *
 * Rules implemented (toggles live in {@link CrazyEightsConfig}):
 *  - One 52-card deck, `handSize` cards each, one card turned up to start the discard pile (an eight is buried instead).
 *  - On your turn play a card that matches the top card's suit or rank. Any eight is wild: its player names the suit.
 *  - With no playable card you draw from the stock, one card at a time, up to `maxDraws` times, and may play a drawn
 *    card at once. If you still cannot play (or the stock is gone) you pass. An empty stock is refilled from the
 *    discard pile, keeping the top card.
 *  - The first player to empty their hand wins the round and scores the card values left in the other hands
 *    (eight 50, K/Q/J/10 10 each, ace 1, others face value). If every player passes in a row the round is blocked: the
 *    lowest hand wins the difference.
 *  - The match ends when a player reaches `targetScore` (highest total wins) or after `maxRounds`; equal totals share
 *    a place.
 */
import { parseCard, removeCards, sortHand, SUITS, buildDeck, type CardId, type Suit } from '../../core/cards.js';
import { checkInt, mergeKnown } from '../../core/config.js';
import { shuffle, type Rng } from '../../core/rng.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  standingsFromScores,
  type EngineEvent,
  type GameEngine,
  type Standing,
} from '../../core/types.js';

export interface CrazyEightsConfig {
  /** Match ends when a player reaches this total. */
  targetScore: number;
  /** Hard cap on rounds so a match always terminates. */
  maxRounds: number;
  handSize: number;
  /** Cards a player may draw in one turn when unable to play. */
  maxDraws: number;
  roundEndDelayMs: number;
}

export type CrazyEightsPhase = 'PLAYING' | 'ROUND_END' | 'MATCH_END';

export interface CrazyEightsRoundResult {
  round: number;
  /** Seat that went out (or the lowest hand of a blocked round); null when nobody scored. */
  winner: number | null;
  blocked: boolean;
  /** Points each seat gained this round. */
  gains: number[];
  /** What was left in every hand (public once the round is over). */
  hands: CardId[][];
}

export interface CrazyEightsState {
  config: CrazyEightsConfig;
  players: string[];
  round: number;
  dealer: number;
  phase: CrazyEightsPhase;
  hands: CardId[][];
  stock: CardId[];
  /** Discard pile, the top card last. */
  discard: CardId[];
  /** Suit in force: the top card's suit, or the one named by an eight. */
  activeSuit: Suit;
  turn: number;
  /** Cards drawn by the player to act this turn. */
  draws: number;
  /** Consecutive passes with nothing left to draw. */
  passes: number;
  /** Suits a seat has been seen unable to match (it had to draw). */
  lacks: Suit[][];
  lastPlay: { seat: number; card: CardId } | null;
  scores: number[];
  roundResults: CrazyEightsRoundResult[];
}

export type CrazyEightsAction = { type: 'play'; card: CardId; suit?: Suit } | { type: 'draw' } | { type: 'pass' };

export interface CrazyEightsLegal {
  playable: CardId[];
  canDraw: boolean;
  canPass: boolean;
}

export interface CrazyEightsView {
  gameType: 'CRAZY_EIGHTS';
  config: CrazyEightsConfig;
  seat: number | null;
  phase: CrazyEightsPhase;
  round: number;
  dealer: number;
  turn: number;
  hand: CardId[];
  handCounts: number[];
  stockCount: number;
  discardTop: CardId;
  activeSuit: Suit;
  draws: number;
  lacks: Suit[][];
  lastPlay: { seat: number; card: CardId } | null;
  scores: number[];
  roundResults: CrazyEightsRoundResult[];
  totals: number[];
}

const DEFAULTS: CrazyEightsConfig = {
  targetScore: 150,
  maxRounds: 10,
  handSize: 5,
  maxDraws: 3,
  roundEndDelayMs: 6000,
};

const SEATS = 4;

/** Penalty value of a card left in hand. */
export function crazyEightsValue(card: CardId): number {
  const r = parseCard(card).rank;
  if (r === 8) return 50;
  if (r === 14) return 1;
  if (r >= 10) return 10;
  return r;
}

export const crazyEightsHandValue = (hand: readonly CardId[]): number => hand.reduce((n, c) => n + crazyEightsValue(c), 0);

/** Cards that may be played on `top` with `activeSuit` in force. */
export function crazyEightsPlayable(hand: readonly CardId[], top: CardId, activeSuit: Suit): CardId[] {
  const topRank = parseCard(top).rank;
  return hand.filter((c) => {
    const p = parseCard(c);
    return p.rank === 8 || p.suit === activeSuit || p.rank === topRank;
  });
}

/** True when the seat can still draw: the stock has cards, or the discard pile can be recycled. */
const canRefill = (s: Pick<CrazyEightsState, 'stock' | 'discard'>): boolean => s.stock.length > 0 || s.discard.length > 1;

export function crazyEightsLegal(state: CrazyEightsState, seat: number): CrazyEightsLegal {
  if (state.phase !== 'PLAYING' || seat !== state.turn) return { playable: [], canDraw: false, canPass: false };
  const playable = crazyEightsPlayable(state.hands[seat]!, state.discard.at(-1)!, state.activeSuit);
  if (playable.length > 0) return { playable, canDraw: false, canPass: false };
  const canDraw = state.draws < state.config.maxDraws && canRefill(state);
  return { playable, canDraw, canPass: !canDraw };
}

/** Suit the hand holds most of (ties broken by card value) — the default pick for an eight. */
export function crazyEightsBestSuit(hand: readonly CardId[], exclude?: CardId): Suit {
  let best: Suit = SUITS[0]!;
  let bestScore = -1;
  for (const suit of SUITS) {
    const cards = hand.filter((c) => c !== exclude && parseCard(c).suit === suit && parseCard(c).rank !== 8);
    const score = cards.length * 100 + crazyEightsHandValue(cards);
    if (score > bestScore) {
      bestScore = score;
      best = suit;
    }
  }
  return best;
}

function deal(state: CrazyEightsState, rng: Rng, events: EngineEvent[]): void {
  const deck = shuffle(buildDeck(), rng);
  const hands: CardId[][] = Array.from({ length: SEATS }, () => []);
  for (let i = 0; i < state.config.handSize; i++) for (let s = 0; s < SEATS; s++) hands[s]!.push(deck.pop()!);
  // Turn up a starter that is not an eight; any eights drawn are buried at the bottom of the stock.
  const buried: CardId[] = [];
  let starter = deck.pop()!;
  while (parseCard(starter).rank === 8) {
    buried.push(starter);
    starter = deck.pop()!;
  }
  state.hands = hands.map((h) => sortHand(h));
  state.stock = [...buried, ...deck];
  state.discard = [starter];
  state.activeSuit = parseCard(starter).suit;
  state.turn = (state.dealer + 1) % SEATS;
  state.draws = 0;
  state.passes = 0;
  state.lacks = Array.from({ length: SEATS }, () => []);
  state.lastPlay = null;
  state.phase = 'PLAYING';
  events.push({ type: 'dealt', round: state.round, dealer: state.dealer, counts: state.hands.map((h) => h.length), starter });
}

function endRound(state: CrazyEightsState, winner: number | null, blocked: boolean, events: EngineEvent[]): void {
  const gains = Array<number>(SEATS).fill(0);
  if (winner !== null) {
    const others = state.hands.reduce((n, h, s) => (s === winner ? n : n + crazyEightsHandValue(h)), 0);
    gains[winner] = blocked ? Math.max(0, others - crazyEightsHandValue(state.hands[winner]!)) : others;
    state.scores[winner]! += gains[winner]!;
  }
  state.roundResults.push({ round: state.round, winner, blocked, gains, hands: state.hands.map((h) => [...h]) });
  events.push({ type: 'roundEnd', round: state.round, winner, blocked, gains, scores: [...state.scores] });
  const cfg = state.config;
  const top = Math.max(...state.scores);
  const leaders = state.scores.filter((v) => v === top).length;
  if ((top >= cfg.targetScore && leaders === 1) || state.round >= cfg.maxRounds) {
    state.phase = 'MATCH_END';
    events.push({ type: 'matchEnd', standings: standingsFromScores(state.scores.map((score, seat) => ({ seat, score }))) });
  } else {
    state.phase = 'ROUND_END';
  }
}

export const crazyEightsEngine: GameEngine<CrazyEightsConfig, CrazyEightsState, CrazyEightsAction, CrazyEightsView, CrazyEightsLegal> = {
  gameType: 'CRAZY_EIGHTS',
  kind: 'match',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    checkInt('targetScore', c.targetScore, 20, 2000);
    checkInt('maxRounds', c.maxRounds, 1, 40);
    checkInt('handSize', c.handSize, 3, 8);
    checkInt('maxDraws', c.maxDraws, 1, 10);
    checkInt('roundEndDelayMs', c.roundEndDelayMs, 0, 60000);
    return c;
  },

  seatLimits: () => ({ min: SEATS, max: SEATS }),

  createState(config, seats, rng) {
    assertEngine(seats.length === SEATS, 'INVALID_CONFIG', 'Crazy Eights needs exactly 4 seats');
    const players = Array<string>(SEATS).fill('');
    for (const s of seats) {
      assertEngine(s.seat >= 0 && s.seat < SEATS && !players[s.seat], 'SEAT_TAKEN', `Bad seat ${s.seat}`);
      players[s.seat] = s.playerId;
    }
    const state: CrazyEightsState = {
      config,
      players,
      round: 1,
      dealer: rng.int(SEATS),
      phase: 'PLAYING',
      hands: [],
      stock: [],
      discard: [],
      activeSuit: 'S',
      turn: 0,
      draws: 0,
      passes: 0,
      lacks: [],
      lastPlay: null,
      scores: Array<number>(SEATS).fill(0),
      roundResults: [],
    };
    deal(state, rng, []);
    return state;
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    if (raw.type === 'draw') return { type: 'draw' };
    if (raw.type === 'pass') return { type: 'pass' };
    if (raw.type === 'play' && typeof raw.card === 'string' && raw.card.length <= 4) {
      if (raw.suit === undefined) return { type: 'play', card: raw.card };
      if (typeof raw.suit === 'string' && (SUITS as readonly string[]).includes(raw.suit)) return { type: 'play', card: raw.card, suit: raw.suit as Suit };
    }
    throw new EngineError('INVALID_ACTION', 'Unknown Crazy Eights action');
  },

  applyAction(state, seat, action, rng) {
    assertEngine(state.phase !== 'MATCH_END', 'GAME_OVER', 'Match is over');
    assertEngine(state.phase === 'PLAYING' && seat === state.turn, 'NOT_YOUR_TURN', 'Not your turn');
    const events: EngineEvent[] = [];
    const hand = state.hands[seat]!;
    const legal = crazyEightsLegal(state, seat);

    if (action.type === 'draw') {
      assertEngine(legal.canDraw, 'ILLEGAL_MOVE', 'You cannot draw now');
      if (state.stock.length === 0) {
        // Recycle everything under the top card.
        const top = state.discard.pop()!;
        state.stock = shuffle(state.discard, rng);
        state.discard = [top];
        events.push({ type: 'reshuffled', count: state.stock.length });
      }
      const card = state.stock.pop()!;
      hand.push(card);
      state.hands[seat] = sortHand(hand);
      state.draws++;
      state.passes = 0;
      if (!state.lacks[seat]!.includes(state.activeSuit)) state.lacks[seat]!.push(state.activeSuit);
      events.push({ type: 'draw', seat });
      return events;
    }

    if (action.type === 'pass') {
      assertEngine(legal.canPass, 'ILLEGAL_MOVE', 'You cannot pass now');
      state.passes++;
      events.push({ type: 'pass', seat });
      if (state.passes >= SEATS) {
        const values = state.hands.map(crazyEightsHandValue);
        const low = Math.min(...values);
        const lows = values.flatMap((v, s) => (v === low ? [s] : []));
        endRound(state, lows.length === 1 ? lows[0]! : null, true, events);
        return events;
      }
      state.turn = (seat + 1) % SEATS;
      state.draws = 0;
      return events;
    }

    assertEngine(hand.includes(action.card), 'INVALID_CARD', 'Card not in hand');
    assertEngine(legal.playable.includes(action.card), 'ILLEGAL_MOVE', 'That card cannot be played now');
    const played = parseCard(action.card);
    if (played.rank === 8) assertEngine(action.suit !== undefined, 'INVALID_ACTION', 'Name a suit for the eight');
    removeCards(hand, [action.card]);
    state.discard.push(action.card);
    state.activeSuit = played.rank === 8 ? action.suit! : played.suit;
    state.lastPlay = { seat, card: action.card };
    state.passes = 0;
    events.push({ type: 'cardPlayed', seat, card: action.card, suit: state.activeSuit });
    if (hand.length === 0) {
      endRound(state, seat, false, events);
      return events;
    }
    state.turn = (seat + 1) % SEATS;
    state.draws = 0;
    return events;
  },

  getActors(state) {
    return state.phase === 'PLAYING' ? [state.turn] : [];
  },

  getLegalActions(state, seat) {
    return crazyEightsLegal(state, seat);
  },

  getTimeoutAction(state, seat) {
    const legal = crazyEightsLegal(state, seat);
    if (legal.playable.length > 0) {
      // Keep eights for when they are needed.
      const card = [...legal.playable].sort((a, b) => Number(parseCard(a).rank === 8) - Number(parseCard(b).rank === 8) || crazyEightsValue(b) - crazyEightsValue(a))[0]!;
      return parseCard(card).rank === 8 ? { type: 'play', card, suit: crazyEightsBestSuit(state.hands[seat]!, card) } : { type: 'play', card };
    }
    return legal.canDraw ? { type: 'draw' } : { type: 'pass' };
  },

  getView(state, seat) {
    return {
      gameType: 'CRAZY_EIGHTS',
      config: state.config,
      seat,
      phase: state.phase,
      round: state.round,
      dealer: state.dealer,
      turn: state.turn,
      hand: seat === null ? [] : [...state.hands[seat]!],
      handCounts: state.hands.map((h) => h.length),
      stockCount: state.stock.length,
      discardTop: state.discard.at(-1)!,
      activeSuit: state.activeSuit,
      draws: state.draws,
      lacks: state.lacks.map((l) => [...l]),
      lastPlay: state.lastPlay ? { ...state.lastPlay } : null,
      scores: [...state.scores],
      roundResults: state.roundResults.map((r) => ({ ...r, gains: [...r.gains], hands: r.hands.map((h) => [...h]) })),
      totals: [...state.scores],
    };
  },

  getSystemStep(state) {
    return state.phase === 'ROUND_END' ? { kind: 'nextRound', delayMs: state.config.roundEndDelayMs } : null;
  },

  runSystemStep(state, rng) {
    const events: EngineEvent[] = [];
    if (state.phase === 'ROUND_END') {
      state.round++;
      state.dealer = (state.dealer + 1) % SEATS;
      deal(state, rng, events);
    }
    return events;
  },

  isFinished: (state) => state.phase === 'MATCH_END',

  getStandings(state): Standing[] {
    return standingsFromScores(state.scores.map((score, seat) => ({ seat, score })));
  },

  getRoundsCompleted: (state) => state.roundResults.length,
};
